'use client';

import React, { useCallback, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_POSITION } from '@/constants';
import { useNewsletterSubscribe } from '@/hooks/useNewsletterSubscribe.hook';
import { markNewsletterSubscribed } from '@/utils/newsletter-popup-storage';

// 완료 모달은 motion 을 쓰므로 지연 로드한다 — 구독하지 않는 대다수 방문자의
// 블로그 상세 초기 번들에 모션 런타임이 얹히지 않게 한다
const NewsletterSubscribedModal = dynamic(
  () => import('@/components/newsletter-subscribed-modal'),
  { ssr: false },
);

/**
 * 좌 레일 뉴스레터 구독 박스 (Figma 3710:11091) + 구독 완료 모달.
 *
 * 본문 폭 버전(`@/components/newsletter-subscribe`)과 마크업을 공유하지 않는다 —
 * 이유는 그쪽 파일 주석에 있다. 폼 로직은 양쪽 모두 `useNewsletterSubscribe` 를 쓴다.
 *
 * 여기서 구독해도 모바일 팝업의 "구독 완료" 플래그를 세워 두 경로가 같은 이력을
 * 공유한다.
 */
export default function RailNewsletterSubscribe() {
  const t = useTranslations('Blog.newsletter');
  const [isSubscribedOpen, setIsSubscribedOpen] = useState(false);

  const handleSubscribed = useCallback(() => {
    markNewsletterSubscribed();
    setIsSubscribedOpen(true);
  }, []);

  // 완료 모달의 자동 닫힘 타이머가 리렌더마다 초기화되지 않게 참조를 고정한다
  const closeSubscribed = useCallback(() => setIsSubscribedOpen(false), []);

  const { email, messageType, isLoading, handleEmailChange, handleSubmit } =
    useNewsletterSubscribe({
      leadLabel: 'newsletter',
      source: 'newsletter_subscribe',
      onSubscribed: handleSubscribed,
    });

  const title = useMemo(() => t.rich('title', { br: () => <br /> }), [t]);
  const placeholder = t('placeholder');
  const buttonText = isLoading ? t('loading') : t('button');
  // 성공은 완료 모달이 알리므로 여기 인라인 메시지는 실패 사유만 담는다
  const message = messageType ? t(`message.${messageType}`) : null;

  return (
    <>
      <section className="flex w-full flex-col gap-3.5 rounded-[20px] bg-3b-background-light px-4 py-[18px]">
        {/* Figma Caption/600 */}
        <h2 className="text-center text-[14px] font-semibold leading-[18px] tracking-[0.016px] text-black">
          {title}
        </h2>
        <form onSubmit={handleSubmit} className="flex w-full flex-col gap-3.5">
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder={placeholder}
            aria-label={placeholder}
            aria-invalid={messageType === 'invalid-email' || undefined}
            aria-describedby={message ? 'rail-newsletter-message' : undefined}
            autoComplete="email"
            className="h-10 w-full rounded-[8px] bg-white px-3 text-[12px] tracking-[0.016px] text-3b-text-primary placeholder:text-3b-text-hint"
          />
          {/* label 은 배치와 무관하게 하나로 두고 position 으로 구분한다 —
              본문 폭 버전(`@/components/newsletter-subscribe`)이 같은 label 을
              `main_content` 로 쓴다 */}
          <TrackedButton
            label="newsletter_subscribe"
            position={TRACKED_EVENT_POSITION.LEFT_SIDEBAR}
            type="submit"
            disabled={isLoading}
            // Figma Button/Small/500 + Accent/Primary(= 3b-blue-light-900 #040435)
            className="w-full rounded-[8px] bg-3b-blue-light-900 py-3 text-[14px] font-medium leading-[12px] tracking-[-0.04px] text-3b-text-contrast hover:bg-3b-blue-light-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {buttonText}
          </TrackedButton>
        </form>
        {message ? (
          <p
            id="rail-newsletter-message"
            role="alert"
            className="text-center text-caption text-red-600"
          >
            {message}
          </p>
        ) : null}
      </section>
      {/* 열릴 때만 렌더해야 dynamic 이 실제로 지연된다 — 닫힌 채로 렌더하면
          마운트 시점에 청크를 바로 받아 지연 로드가 무의미해진다 */}
      {isSubscribedOpen ? (
        <NewsletterSubscribedModal isOpen onClose={closeSubscribed} />
      ) : null}
    </>
  );
}
